import React, { useCallback, useEffect } from 'react'
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_EDITOR, LexicalCommand } from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'

import FloatingToolbarPlugin from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/FloatingToolbarPlugin/FloatingToolbarPlugin'
import { ReadOnlyLexicalEditor } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { getEditorStateFromText, getHtmlContentFromState } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { LEXICAL_NODES } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { isFirefox } from '../../apps/professional-app/fa/MeetingSummary/utils'
import { HTML_TYPE } from '../../common/copy'

import { MissingScopeAction, ScopeDescriptor } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { Button } from '../../components/Button/Button'

export interface SelectionHandler {
  id: string
  label: string
  scope?: ScopeDescriptor
  lexicalCommand: LexicalCommand<void>
  handle: (selection: string) => Promise<void>
}

interface TaskDisplayProps {
  lexicalValue: string
  selectionHandlers?: SelectionHandler[]
}

export const TaskDisplay: React.FC<TaskDisplayProps> = ({ lexicalValue, selectionHandlers }) => {
  return (
    <ReadOnlyLexicalEditor initialEditorState={lexicalValue} lexicalNodes={LEXICAL_NODES}>
      {selectionHandlers ? (
        <>
          <FloatingToolbarPlugin>
            <FloatingToolbarContent handlers={selectionHandlers} />
          </FloatingToolbarPlugin>
          <SelectionHandlerPlugin handlers={selectionHandlers} />
        </>
      ) : null}
    </ReadOnlyLexicalEditor>
  )
}

interface FloatingToolbarContentProps {
  handlers: SelectionHandler[]
}

const FloatingToolbarContent: React.FC<FloatingToolbarContentProps> = ({ handlers }) => {
  const [editor] = useLexicalComposerContext()

  return (
    <>
      {handlers.map(({ id, label, scope, lexicalCommand }) => (
        <ScopedBlock key={id} scope={scope} missingScopeAction={MissingScopeAction.DISABLE}>
          <Button
            variant='borderless'
            onClick={() => editor.dispatchCommand(lexicalCommand, undefined)}
            className='button-small zocks-p-0.75 floating-toolbar-button'
            aria-label={label}
          >
            <i className='bi bi-journal-text' />
            {label}
          </Button>
        </ScopedBlock>
      ))}
    </>
  )
}

interface SelectionHandlerPluginProps {
  handlers: SelectionHandler[]
}

const SelectionHandlerPlugin: React.FC<SelectionHandlerPluginProps> = ({ handlers }) => {
  const [editor] = useLexicalComposerContext()

  const getHtmlContent = async () => {
    if (isFirefox()) {
      const text = await navigator.clipboard.readText()
      const editorState = getEditorStateFromText(text)
      return getHtmlContentFromState(JSON.stringify(editorState))
    }

    const clipboardContents = await navigator.clipboard.read()
    const htmlContentItem = clipboardContents.find((item) => item.types.includes(HTML_TYPE))
    if (!htmlContentItem) {
      return ''
    }
    const htmlBlob = await htmlContentItem.getType(HTML_TYPE)
    return await htmlBlob.text()
  }

  const onSelection = useCallback(async (handle: (selection: string) => Promise<void>) => {
    window.document.execCommand('copy')

    const selection = await getHtmlContent()
    if (!selection) {
      return
    }

    await handle(selection)
  }, [])

  useEffect(() => {
    const cleanUpFunctions = handlers.map(({ lexicalCommand, handle }) => {
      return editor.registerCommand(
        lexicalCommand,
        () => {
          const selection = $getSelection()

          if ($isRangeSelection(selection)) {
            onSelection(handle).then(async () => {
              const domSelection = window.getSelection()
              if (domSelection !== null) {
                domSelection.removeAllRanges()
              }
              await navigator.clipboard.writeText('')
            })
          }

          return true
        },
        COMMAND_PRIORITY_EDITOR,
      )
    })

    return () => {
      cleanUpFunctions.forEach((cleanup) => cleanup())
    }
  }, [editor, handlers, onSelection])

  return null
}
